import { MouseEvent, useState } from 'react';
import { Button, Menu, MenuItem, Tooltip } from '@mui/material';
import { TravelExplore } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { CaseResponseDto } from '@bandido/shared/dtos';

import { CONFIG } from '@frontend/config';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { routes } from '@frontend/router/routes';
import {
  caseCanStartTravelSearch,
  operatingAgencyId,
  withBookingCase,
} from '@frontend/features/travel-services/bookingCaseContext';

const VERTICALS = [
  ['hotels', routes.hotels.home, 'Hotels'],
  ['transfers', routes.transfers, 'TravelServices'],
  ['experiences', routes.experiences, 'TravelServices'],
  ['circuits', routes.circuits, 'TravelServices'],
  ['rail', routes.rail, 'TravelServices'],
  ['buses', routes.buses, 'TravelServices'],
  ['ferry', routes.ferry, 'TravelServices'],
  ['passes', routes.passes, 'TravelServices'],
  ['esims', routes.esims, 'TravelServices'],
  ['cars', routes.cars, 'Cars'],
] as const;

export function CaseTravelSearchMenu({ caseData }: { caseData: CaseResponseDto }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { ability } = useAuth();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const agencyId = operatingAgencyId(CONFIG.AGENCY_ID);
  const accessibleVerticals = VERTICALS.filter(([, , subject]) => ability?.can('view', subject));
  const allowed = caseCanStartTravelSearch(
    caseData,
    agencyId,
    accessibleVerticals.length > 0,
  );

  if (!accessibleVerticals.length) return null;

  const openMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchor(event.currentTarget);
  };

  const disabledReason = !caseData.clientId
    ? t('travelServices.caseContext.caseNeedsClient')
    : caseData.agencyId !== agencyId
      ? t('travelServices.caseContext.wrongAgency')
      : '';

  return (
    <>
      <Tooltip title={disabledReason}>
        <span>
          <Button fullWidth variant="contained" startIcon={<TravelExplore />} disabled={!allowed} onClick={openMenu}>
            {t('travelServices.caseContext.searchServices')}
          </Button>
        </span>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {accessibleVerticals.map(([key, route]) => (
          <MenuItem key={key} onClick={() => navigate(withBookingCase(route, caseData.id))}>
            {t(`common.nav.${key}`)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
